import styles from "./line-width-slider.module.css";
import { noop } from "utils/fn-utils";
import { useRef, useEffect, useState } from "react";

export function LineWidth({
  value,
  onChange,
  start = 1,
  step = 1,
  count = 10,
}: {
  value: number;
  onChange: (value: number) => void;
  start?: number;
  step?: number;
  count?: number;
}) {
  return (
    <InputRangeWithMarks
      value={value}
      onChange={onChange}
      min={start}
      step={step}
      max={start + count - 1}
      onFinish={noop}
    />
  );
}

export function InputRangeWithMarks({
  value,
  min = 1,
  max = 10,
  step = 1,
  onChange,
  onFinish,
  backgroundColor = "#454545",
  foregroundColor = "#FFFFFF",
}: {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
  onFinish: (value: number) => void;
  backgroundColor?: string;
  foregroundColor?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [innerValue, setInnerValue] = useState(value);
  const count = max - min; // markers appear at start and end, so their number is count+1
  const valuePct = Math.round((100 * (innerValue - min)) / count);

  // Subscribe to the native onChange event, which is triggered only when user really finished working
  // with the slider.
  // React's onChange property is actually onInput event !
  useEffect(() => {
    if (ref.current) {
      const handler = (e: Event) => {
        onFinish((e.currentTarget as any).valueAsNumber);
      };
      ref.current.addEventListener("change", handler);
      return () => void ref.current?.removeEventListener("change", handler);
    }
  }, []);

  const onInput = (e: React.FormEvent<HTMLInputElement>) => {
    setInnerValue(e.currentTarget.valueAsNumber);
    onChange(e.currentTarget.valueAsNumber);
  };

  const markers = Array.from({ length: count });

  return (
    <div style={{ position: "relative", width: "100%", height: "16px" }}>
      {/* 1. Visual Layer (SVG)
            Pointer events none so clicks pass through to input.
        */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height={"100%"}
        width={"100%"}
        style={{ position: "absolute", inset: "0", pointerEvents: "none", overflow: "visible" }}
      >
        <line x1="0%" y1="50%" x2="100%" y2="50%" strokeWidth={1} stroke={backgroundColor} />
        <line x1="0%" y1="50%" x2={valuePct + "%"} y2="50%" strokeWidth={1} stroke={foregroundColor} />
        {markers.map((_, index) => (
          <circle
            key={index}
            r={2}
            cx={Math.round((100 * index) / count) + "%"}
            cy="50%"
            fill={index * step + min <= innerValue ? foregroundColor : backgroundColor}
          />
        ))}
        <circle cx={valuePct + "%"} cy="50%" r="3" fill={foregroundColor} />
      </svg>

      {/* 2. Invisible Native Input. Occupies full space, handles all interactions. */}
      <input
        ref={ref}
        className={styles.widthSlider}
        type="range"
        min={min}
        max={max}
        step={step}
        value={innerValue}
        onInput={onInput}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={innerValue}
        // aria-label={label}
      />
    </div>
  );
}
